<script>
import { mapActions, mapState } from 'vuex';
import { ReloadOutlined } from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import bem from '@/utils/bem';
import PageHeader from '@/components/ux/pageHeader/page-header.vue';

const b = bem('analytics-dashboard');

export default {
  components: {
    PageHeader,
    ReloadOutlined,
  },
  data() {
    return {
      title: 'Analytics Dashboard',
      period: '7d',
      dateRange: [],
      periodOptions: [
        { value: 'today', label: 'Today' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: '7d', label: 'Last 7 days' },
        { value: '30d', label: 'Last 30 days' },
        { value: 'custom', label: 'Custom' },
      ],
    };
  },
  computed: {
    ...mapState({
      overview: (state) => state.analytics.overview,
      overviewLoading: (state) => state.analytics.overviewLoading,
      errorTrend: (state) => state.analytics.errorTrend,
      errorTrendLoading: (state) => state.analytics.errorTrendLoading,
      errorsByType: (state) => state.analytics.errorsByType,
      errorsByTypeLoading: (state) => state.analytics.errorsByTypeLoading,
      errorsByBrowser: (state) => state.analytics.errorsByBrowser,
      errorsByBrowserLoading: (state) => state.analytics.errorsByBrowserLoading,
      errorsByPage: (state) => state.analytics.errorsByPage,
      errorsByPageLoading: (state) => state.analytics.errorsByPageLoading,
    }),
    filterParams() {
      const params = { period: this.period };
      if (this.period === 'custom' && this.dateRange.length === 2) {
        params.startDate = this.dateRange[0].toISOString();
        params.endDate = this.dateRange[1].toISOString();
      }
      return params;
    },
    sessionStats() {
      if (!this.overview?.sessions) return [];
      const { sessions } = this.overview;
      return [
        { title: 'Total Sessions', value: sessions.total, color: '#1890ff' },
        { title: 'Unique Users', value: sessions.uniqueUsers, color: '#52c41a' },
        { title: 'Sessions with Errors', value: sessions.withErrors, color: '#ff4d4f' },
        { title: 'Avg Duration', value: this.formatDuration(sessions.avgDurationMs), color: '#722ed1' },
      ];
    },
    errorStats() {
      if (!this.overview?.errors) return [];
      const { errors } = this.overview;
      return [
        { title: 'Frontend Errors', value: errors.frontend, color: '#ff4d4f' },
        { title: 'API Errors', value: errors.api, color: '#fa8c16' },
        { title: 'Server Errors (5xx)', value: errors.serverErrors, color: '#eb2f96' },
        { title: 'Client Errors (4xx)', value: errors.clientErrors, color: '#faad14' },
      ];
    },
    clickStats() {
      if (!this.overview?.clicks) return [];
      const { clicks } = this.overview;
      return [
        { title: 'Total Clicks', value: clicks.total, color: '#1890ff' },
        { title: 'Rage Clicks', value: clicks.rage, color: '#ff4d4f' },
        { title: 'Dead Clicks', value: clicks.dead, color: '#faad14' },
      ];
    },
    errorTrendChartData() {
      if (!this.errorTrend?.trend) return { labels: [], datasets: [] };
      const { trend } = this.errorTrend;
      return {
        labels: trend.map((t) => dayjs(t.time).format('MM/DD HH:mm')),
        datasets: [
          {
            label: 'Total Errors',
            data: trend.map((t) => t.total),
            borderColor: '#ff4d4f',
            backgroundColor: 'rgba(255, 77, 79, 0.1)',
            fill: true,
          },
          {
            label: 'Frontend',
            data: trend.map((t) => t.frontend),
            borderColor: '#1890ff',
            backgroundColor: 'rgba(24, 144, 255, 0.1)',
            fill: false,
          },
          {
            label: 'API',
            data: trend.map((t) => t.api),
            borderColor: '#fa8c16',
            backgroundColor: 'rgba(250, 140, 22, 0.1)',
            fill: false,
          },
        ],
      };
    },
    browserChartData() {
      if (!this.errorsByBrowser?.browsers) return [];
      return this.errorsByBrowser.browsers.map((item) => ({
        name: item.browser,
        value: item.count,
      }));
    },
    frontendErrorsByType() {
      if (!this.errorsByType?.frontendByType) return [];
      return this.errorsByType.frontendByType;
    },
    frontendErrorsByPage() {
      if (!this.errorsByPage?.frontendByPage) return [];
      return this.errorsByPage.frontendByPage;
    },
    apiErrorsByEndpoint() {
      if (!this.errorsByPage?.apiByEndpoint) return [];
      return this.errorsByPage.apiByEndpoint;
    },
  },
  methods: {
    b,
    ...mapActions('analytics', [
      'getOverview',
      'getErrorTrend',
      'getErrorsByType',
      'getErrorsByBrowser',
      'getErrorsByPage',
    ]),
    formatDuration(ms) {
      if (!ms) return '0s';
      const seconds = Math.floor(ms / 1000);
      const minutes = Math.floor(seconds / 60);
      if (minutes > 0) {
        return `${minutes}m ${seconds % 60}s`;
      }
      return `${seconds}s`;
    },
    loadData() {
      const params = this.filterParams;
      this.getOverview(params);
      this.getErrorTrend(params);
      this.getErrorsByType(params);
      this.getErrorsByBrowser(params);
      this.getErrorsByPage(params);
    },
    onPeriodChange() {
      if (this.period !== 'custom') {
        this.dateRange = [];
        this.loadData();
      }
    },
    onDateRangeChange() {
      if (this.dateRange.length === 2) {
        this.loadData();
      }
    },
    goToSessions() {
      this.$router.push('/analytics/sessions');
    },
    goToFrontendErrors() {
      this.$router.push('/analytics/frontend-errors');
    },
    goToHeatmaps() {
      this.$router.push('/analytics/heatmaps');
    },
    goToAlerts() {
      this.$router.push('/analytics/alerts');
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<template>
  <div :class="b()">
    <page-header :title="title">
      <template #actions>
        <a-space>
          <a-select
            v-model:value="period"
            :options="periodOptions"
            style="width: 140px"
            @change="onPeriodChange"
          />
          <a-range-picker
            v-if="period === 'custom'"
            v-model:value="dateRange"
            :show-time="false"
            @change="onDateRangeChange"
          />
          <a-button @click="loadData">
            <reload-outlined />
            Refresh
          </a-button>
        </a-space>
      </template>
    </page-header>

    <!-- Quick Navigation -->
    <a-row :gutter="[16, 16]" :class="b('nav-cards')">
      <a-col :xs="24" :sm="12" :md="6">
        <a-card hoverable @click="goToSessions">
          <a-statistic
            title="Session Replay"
            :value="overview?.sessions?.total || 0"
            suffix="sessions"
          />
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="12" :md="6">
        <a-card hoverable @click="goToFrontendErrors">
          <a-statistic
            title="Frontend Errors"
            :value="overview?.errors?.frontend || 0"
            suffix="errors"
            :value-style="{ color: '#ff4d4f' }"
          />
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="12" :md="6">
        <a-card hoverable @click="goToHeatmaps">
          <a-statistic
            title="Heatmaps"
            :value="overview?.clicks?.total || 0"
            suffix="clicks"
          />
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="12" :md="6">
        <a-card hoverable @click="goToAlerts">
          <a-statistic
            title="Alerts"
            :value="overview?.errors?.sessionsAffected || 0"
            suffix="affected"
            :value-style="{ color: '#fa8c16' }"
          />
        </a-card>
      </a-col>
    </a-row>

    <!-- Session Stats -->
    <a-card title="Sessions Overview" :loading="overviewLoading" :class="b('section')">
      <a-row :gutter="[16, 16]">
        <a-col v-for="stat in sessionStats" :key="stat.title" :xs="12" :sm="6">
          <a-statistic
            :title="stat.title"
            :value="stat.value"
            :value-style="{ color: stat.color }"
          />
        </a-col>
      </a-row>
    </a-card>

    <!-- Error Stats -->
    <a-card title="Error Overview" :loading="overviewLoading" :class="b('section')">
      <a-row :gutter="[16, 16]">
        <a-col v-for="stat in errorStats" :key="stat.title" :xs="12" :sm="6">
          <a-statistic
            :title="stat.title"
            :value="stat.value"
            :value-style="{ color: stat.color }"
          />
        </a-col>
      </a-row>
    </a-card>

    <!-- Click Stats -->
    <a-card title="Click Analysis" :loading="overviewLoading" :class="b('section')">
      <a-row :gutter="[16, 16]">
        <a-col v-for="stat in clickStats" :key="stat.title" :xs="12" :sm="8">
          <a-statistic
            :title="stat.title"
            :value="stat.value"
            :value-style="{ color: stat.color }"
          />
        </a-col>
      </a-row>
    </a-card>

    <!-- Charts Row -->
    <a-row :gutter="[16, 16]" :class="b('section')">
      <!-- Error Trend -->
      <a-col :xs="24" :lg="12">
        <a-card title="Error Trend" :loading="errorTrendLoading">
          <div :class="b('chart-placeholder')">
            <p>Error trend chart will be rendered here with a charting library (e.g., ECharts)</p>
            <p v-if="errorTrend">
              Data points: {{ errorTrend.trend?.length || 0 }}
            </p>
          </div>
        </a-card>
      </a-col>

      <!-- Errors by Browser -->
      <a-col :xs="24" :lg="12">
        <a-card title="Errors by Browser" :loading="errorsByBrowserLoading">
          <a-table
            :dataSource="browserChartData"
            :columns="[
              { title: 'Browser', dataIndex: 'name', key: 'name' },
              { title: 'Errors', dataIndex: 'value', key: 'value' },
            ]"
            :pagination="false"
            size="small"
          />
        </a-card>
      </a-col>
    </a-row>

    <!-- Error Details Row -->
    <a-row :gutter="[16, 16]" :class="b('section')">
      <!-- Errors by Type -->
      <a-col :xs="24" :lg="12">
        <a-card title="Frontend Errors by Type" :loading="errorsByTypeLoading">
          <a-table
            :dataSource="frontendErrorsByType"
            :columns="[
              { title: 'Error Type', dataIndex: 'errorType', key: 'errorType' },
              { title: 'Count', dataIndex: 'count', key: 'count' },
            ]"
            :pagination="false"
            size="small"
          />
        </a-card>
      </a-col>

      <!-- Errors by Page -->
      <a-col :xs="24" :lg="12">
        <a-card title="Errors by Page" :loading="errorsByPageLoading">
          <a-table
            :dataSource="frontendErrorsByPage"
            :columns="[
              { title: 'Page Path', dataIndex: 'pagePath', key: 'pagePath', ellipsis: true },
              { title: 'Errors', dataIndex: 'count', key: 'count' },
              { title: 'Sessions', dataIndex: 'sessionsAffected', key: 'sessionsAffected' },
            ]"
            :pagination="false"
            size="small"
          />
        </a-card>
      </a-col>
    </a-row>

    <!-- API Errors -->
    <a-card title="Top API Errors" :loading="errorsByPageLoading" :class="b('section')">
      <a-table
        :dataSource="apiErrorsByEndpoint"
        :columns="[
          { title: 'Method', dataIndex: 'method', key: 'method', width: 80 },
          { title: 'Endpoint', dataIndex: 'endpoint', key: 'endpoint', ellipsis: true },
          { title: 'Errors', dataIndex: 'count', key: 'count', width: 100 },
        ]"
        :pagination="false"
        size="small"
      />
    </a-card>
  </div>
</template>

<style lang="scss">
.analytics-dashboard {
  padding: 24px;

  &__nav-cards {
    margin-bottom: 24px;

    .ant-card {
      cursor: pointer;
      transition: all 0.3s;

      &:hover {
        border-color: #1890ff;
        box-shadow: 0 2px 8px rgba(24, 144, 255, 0.2);
      }
    }
  }

  &__section {
    margin-bottom: 16px;
  }

  &__chart-placeholder {
    height: 200px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #fafafa;
    border-radius: 4px;
    color: #999;
  }
}
</style>
